/* eslint-disable jsx-a11y/no-autofocus */
import { Checkbox, Col, Row, Spin } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import List from 'app/components/Core/Category/List'
import CategoryParentList from 'app/components/Core/Category/ParentList'
import CategorySearchEmpty from 'app/components/Core/Category/Search/Empty'
import CategorySegmentList from 'app/components/Core/CategorySegment/List'
import useLineItemCreate from 'app/components/Core/Checkout/hooks/useLineItemCreate'
import LocalAvailabilityHint from 'app/components/Core/Checkout/LocalAvailabilityHint'
import KSearch from 'app/components/Modules/KSearch'
import { RecordType } from 'app/constants/GlobalTypes'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useIndex } from 'app/lib/hooks/useApi'
import { useCurrentCustomer } from 'app/lib/hooks/useCurrent'
import { useQuery } from 'app/lib/hooks/useQuery'
import { useRestriction } from 'app/lib/hooks/useRestriction'
import * as rentals from 'app/queries/Rentals'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import testIds from 'test_ids.json'

type Props = {
  rentalId?: string
}
const MachineSearch: React.FC<Props> = ({ rentalId: checkout_id }) => {
  const [q, setQ] = useState<string>('')
  const [value, setValue] = useState<string>('')
  const [inPriceList, setInPriceList] = useState<boolean>(false)
  const { has_price_list } = useCurrentCustomer()
  const {
    loading,
    execute,
    response: { data }
  } = useIndex<ICategory>(RecordType.category_search)
  const { tRecordType } = ToolBox.useString()
  const { open: openLineItemCreate } = useLineItemCreate({ rentalId: checkout_id })

  const {
    loading: loadingRental,
    data: { rental }
  } = useQuery<{ rental: IRental }>(rentals.FETCH_RENTAL_ADDRESS, {
    fetchPolicy: 'cache-first',
    variables: {
      id: checkout_id
    }
  })
  const {
    drawerfy: { open, close }
  } = useFeedbacker()
  const { restricted } = useRestriction({
    allowFor: ['admin', 'manager', 'member', 'accounting', 'delivery_contact']
  })

  const filters = {
    zip: rental?.deliveryAddress?.zip,
    ...(inPriceList ? { in_price_list: true } : {})
  }

  useEffect(() => {
    if (q.length && rental) execute({ q, ...filters })
  }, [q, JSON.stringify(filters)])

  useEffect(() => {
    onDelayedSearch(value)
  }, [value])

  const onChecked = (e: CheckboxChangeEvent) => setInPriceList(e.target.checked)

  const onCategorySegmentClick = (categorySegment: ICategorySegment) => {
    close()
    openLineItemCreate({ categorySegmentId: categorySegment.id })
  }

  const onCategoryClick = (category: ICategory) => {
    open({
      drawerType: 'page',
      closable: false,
      content: (
        <div data-testid={testIds.checkout.machines.search.category_segment_list}>
          <CategorySegmentList
            collection={category.category_segments}
            props={{ onRowClick: onCategorySegmentClick, showAvailability: !restricted }}
          />
        </div>
      ),
      title: category.label,
      zIndex: 1300,
      width: 600
    })
  }

  const onDelayedSearch = useCallback(
    debounce((q: string) => setQ(q), 400),
    []
  )

  const onSearch = (q: string) => setValue(q)

  const clearSearch = () => setValue('')

  return (
    <Row gutter={[0, 24]} className="mobile-spacing">
      <Col span={24}>
        <Row gutter={[0, 12]}>
          <Col span={24}>
            <span className="text-primary">{tRecordType(RecordType.category, 'search_title')}</span>
          </Col>
          <Col span={24}>
            <KSearch
              loading={loading}
              recordType={RecordType.category}
              onSearch={onSearch}
              autoFocus
              searchDelay={0}
              data-testid={testIds.checkout.machines.search.drawer_placeholder}
              value={value}
            />
          </Col>
          <Col span={24} hidden={!has_price_list}>
            <Checkbox onChange={onChecked} checked={inPriceList}>
              {tRecordType(RecordType.category, 'in_price_list')}
            </Checkbox>
          </Col>
          <Col span={24}>
            <LocalAvailabilityHint rental={rental} />
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Choose>
          <When condition={data.length && q.length > 0}>
            <List
              collection={data.filter((category) => category.category_segments.length > 0)}
              props={{
                onCategorySegmentClick,
                onCategoryClick
              }}
            />
          </When>
          <When condition={q.length === 0 && rental}>
            <CategoryParentList onCategoryClick={onCategoryClick} filters={filters} />
          </When>
          <When condition={loading || loadingRental}>
            <div className="center-content mt-3">
              <Spin spinning />
            </div>
          </When>
          <Otherwise>
            <CategorySearchEmpty onButtonClick={clearSearch} />
          </Otherwise>
        </Choose>
      </Col>
    </Row>
  )
}

export default MachineSearch
